import React from 'react';
import { AlertTriangleIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { FocusTableData } from '../../types';
import { useTheme } from '../../context/ThemeContext';
interface FocusTablesProps {
  focusData: {
    positive: FocusTableData[];
    negative: FocusTableData[];
  };
}
const FocusTables: React.FC<FocusTablesProps> = ({
  focusData
}) => {
  const {
    theme
  } = useTheme();
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'stable':
        return theme === 'dark' ? 'text-green-400' : 'text-green-600';
      case 'minimal':
        return theme === 'dark' ? 'text-green-500' : 'text-green-700';
      case 'routine':
        return theme === 'dark' ? 'text-lime-400' : 'text-lime-600';
      case 'minor':
        return theme === 'dark' ? 'text-yellow-300' : 'text-yellow-600';
      case 'moderate':
        return theme === 'dark' ? 'text-amber-300' : 'text-amber-600';
      case 'major':
        return theme === 'dark' ? 'text-orange-400' : 'text-orange-600';
      case 'needsAttention':
        return theme === 'dark' ? 'text-red-400' : 'text-red-600';
      case 'broken':
        return theme === 'dark' ? 'text-fuchsia-400' : 'text-fuchsia-600';
      default:
        return theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
    }
  };
  const getStatusBg = (status: string) => {
    switch (status) {
      case 'stable':
        return theme === 'dark' ? 'bg-green-900/30' : 'bg-green-100';
      case 'minimal':
        return theme === 'dark' ? 'bg-green-800/30' : 'bg-green-200';
      case 'routine':
        return theme === 'dark' ? 'bg-lime-900/30' : 'bg-lime-100';
      case 'minor':
        return theme === 'dark' ? 'bg-yellow-900/30' : 'bg-yellow-100';
      case 'moderate':
        return theme === 'dark' ? 'bg-amber-900/30' : 'bg-amber-100';
      case 'major':
        return theme === 'dark' ? 'bg-orange-900/30' : 'bg-orange-100';
      case 'needsAttention':
        return theme === 'dark' ? 'bg-red-900/30' : 'bg-red-100';
      case 'broken':
        return theme === 'dark' ? 'bg-fuchsia-900/30' : 'bg-fuchsia-100';
      default:
        return theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100';
    }
  };
  // Extract ID and Name from combined string (e.g. "001 Aetna" -> { id: "001", name: "Aetna" })
  const extractNameAndId = (scraperName: string) => {
    const nameParts = scraperName.split(' ');
    const id = nameParts[0];
    const name = nameParts.slice(1).join(' ');
    return {
      id,
      name
    };
  };
  // Filter out stable scrapers from both tables
  const filteredNegative = focusData.negative.filter(item => item.status !== 'stable');
  const filteredPositive = focusData.positive.filter(item => item.status !== 'stable');
  return <div className="grid grid-cols-1 gap-6" data-id="element-97">
      {filteredNegative.length > 0 && <div className="overflow-x-auto" data-id="element-98">
          <table className="w-full" data-id="element-99">
            <thead className={theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'} data-id="element-100">
              <tr data-id="element-101">
                <th className="px-4 py-2 text-left text-sm font-medium w-16" data-id="element-102">
                  ID
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-103">
                  Scraper
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-104">
                  Change
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-105">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-106">
                  Time
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-107">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30" data-id="element-108">
              {filteredNegative.map(item => {
            const {
              id,
              name
            } = extractNameAndId(item.scraperName);
            return <tr key={`${item.scraperId}-${item.date}-${item.amPm}`} className={`${theme === 'dark' ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`} data-id="element-109">
                    <td className="px-4 py-3 text-sm" data-id="element-110">{id}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-111">{name}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-112">
                      <div className="flex items-center" data-id="element-113">
                        <TrendingDownIcon size={14} className={`${getStatusColor(item.status)} mr-1`} data-id="element-114" />
                        <span className={getStatusColor(item.status)} data-id="element-115">
                          {item.urlChange} URLs
                        </span>
                        <span className={`ml-1 ${getStatusColor(item.status)}`} data-id="element-116">
                          ({item.percentChange}%)
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm" data-id="element-117">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusBg(item.status)} ${getStatusColor(item.status)}`} data-id="element-118">
                        {item.status === 'needsAttention' ? 'Needs Attention' : item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" data-id="element-119">{item.amPm}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-120">{item.date}</td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
      {filteredPositive.length > 0 && <div className="overflow-x-auto" data-id="element-121">
          <table className="w-full" data-id="element-122">
            <thead className={theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'} data-id="element-123">
              <tr data-id="element-124">
                <th className="px-4 py-2 text-left text-sm font-medium w-16" data-id="element-125">
                  ID
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-126">
                  Scraper
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-127">
                  Change
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-128">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-129">
                  Time
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-130">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30" data-id="element-131">
              {filteredPositive.map(item => {
            const {
              id,
              name
            } = extractNameAndId(item.scraperName);
            return <tr key={`${item.scraperId}-${item.date}-${item.amPm}`} className={`${theme === 'dark' ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`} data-id="element-132">
                    <td className="px-4 py-3 text-sm" data-id="element-133">{id}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-134">{name}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-135">
                      <div className="flex items-center" data-id="element-136">
                        <TrendingUpIcon size={14} className={`${getStatusColor(item.status)} mr-1`} data-id="element-137" />
                        <span className={getStatusColor(item.status)} data-id="element-138">
                          +{item.urlChange} URLs
                        </span>
                        <span className={`ml-1 ${getStatusColor(item.status)}`} data-id="element-139">
                          (+{item.percentChange}%)
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm" data-id="element-140">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusBg(item.status)} ${getStatusColor(item.status)}`} data-id="element-141">
                        {item.status === 'needsAttention' ? 'Needs Attention' : item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" data-id="element-142">{item.amPm}</td>
                    <td className="px-4 py-3 text-sm" data-id="element-143">{item.date}</td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
    </div>;
};
export default FocusTables;